import React, { useState } from 'react';
import { Modal, Tabs, Empty } from 'antd';
import { Eye } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { REACTIONS, reactionSummary, reactorsOf, useSocialState, viewersOf, type Me, type Post } from '../socialStore';

export type EngagementTab = 'all' | 'views' | string;

function Row({ name, avatar, isMe, badge }: { name: string; avatar: string | null; isMe: boolean; badge?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="relative">
        <UserAvatar name={name} src={avatar} size={36} />
        {badge && <span className="absolute -bottom-1 -right-1 text-sm leading-none">{badge}</span>}
      </div>
      <span className="flex-1 text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{name}{isMe && <span className="font-normal text-gray-400"> (Bạn)</span>}</span>
    </div>
  );
}

/** Ai đã thả cảm xúc (theo từng loại) và ai đã xem bài */
export function EngagementModal({ post, me, open, initialTab, onClose }: { post: Post; me: Me; open: boolean; initialTab: EngagementTab; onClose: () => void }) {
  const { users } = useSocialState();
  const [tab, setTab] = useState<EngagementTab>(initialTab);
  const { total, top } = reactionSummary(post, me.id);
  const viewers = viewersOf(post, users);
  const label = (emoji: string) => REACTIONS.find(r => r.emoji === emoji)?.label || emoji;
  // Bài từ API mới có số tổng; danh sách ai đã thả cảm xúc / đã xem chờ API Đợt 5
  const pending = !!post.stats;
  const pendingList = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Danh sách chi tiết đang được hoàn thiện, sẽ sớm có." />;

  const reactionList = (emoji?: string) => {
    if (pending) return pendingList;
    const list = reactorsOf(post, users, emoji).sort((a, b) => Number(b.id === me.id) - Number(a.id === me.id) || a.name.localeCompare(b.name));
    return list.length
      ? <div className="max-h-[50vh] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">{list.map(r => <Row key={r.id} name={r.name} avatar={r.avatar} isMe={r.id === me.id} badge={r.emoji} />)}</div>
      : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có ai thả cảm xúc" />;
  };

  const items = [
    { key: 'all', label: `Tất cả ${total}`, children: reactionList() },
    ...top.map(t => ({ key: t.emoji, label: <span title={label(t.emoji)}>{t.emoji} {t.count}</span>, children: reactionList(t.emoji) })),
    {
      key: 'views',
      label: <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Đã xem {post.views}</span>,
      children: pending ? pendingList : (
        <div className="max-h-[50vh] overflow-y-auto">
          {viewers.list.length > 0 && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {viewers.list.map(v => <Row key={v.id} name={v.name} avatar={v.avatar} isMe={v.id === me.id} />)}
            </div>
          )}
          {viewers.earlier > 0 && (
            <div className="text-xs text-gray-500 mt-2">+ {viewers.earlier} lượt xem trước khi hệ thống ghi nhận danh sách người xem.</div>
          )}
          {!viewers.list.length && !viewers.earlier && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có ai xem" />}
        </div>
      ),
    },
  ];

  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Tương tác với bài viết" width={440} destroyOnHidden>
      <Tabs activeKey={items.some(i => i.key === tab) ? tab : 'all'} onChange={setTab} items={items} size="small" />
    </Modal>
  );
}
